import {graphql} from 'gatsby'
import React from 'react'

import {Section} from '@/styles/grid'

import type {VatCalculatorFragment} from '@/graphql-types'

import {CalculationForm} from './calculation-form'
import {ResultsList} from './results-list'

const initialState = {
    results: [] as Result[],
}

export type State = typeof initialState

export enum Actions {
    ADD_RESULT = 'ADD_RESULT',
    REMOVE_RESULT = 'REMOVE_RESULT',
}

type Result = {
    amount: number
    vatRate: number
    total: number
    isVatIncluded: boolean
    id: number
}

type AddResult = {
    type: Actions.ADD_RESULT
    value: Result
}

type RemoveResult = {
    type: Actions.REMOVE_RESULT
    value: number
}

type ActionType = AddResult | RemoveResult

export type Dispatch = React.Dispatch<ActionType>

const reducer = (state: State, action: ActionType) => {
    switch (action.type) {
        case Actions.ADD_RESULT: {
            return {
                results: [action.value, ...state.results],
            }
        }

        case Actions.REMOVE_RESULT: {
            const newResults = state.results.filter(({id}) => id !== action.value)

            return {
                results: newResults,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

interface Props {
    content: VatCalculatorFragment['data']
}

export const VatCalculator = ({content}: Props) => {
    const [state, dispatch] = React.useReducer(reducer, initialState)

    const {
        title,
        body,
        amount_label,
        vat_label,
        add_vat_label,
        remove_vat_label,
        total_gross_label,
        total_net_label,
        submit_label,
        results_vat_added_label,
        results_vat_removed_label,
        results_title,
    } = content

    return (
        <Section>
            <CalculationForm
                dispatch={dispatch}
                title={title}
                body={body.richText}
                amountLabel={amount_label}
                vatLabel={vat_label}
                addVatLabel={add_vat_label}
                removeVatLabel={remove_vat_label}
                totalGrossLabel={total_gross_label}
                totalNetLabel={total_net_label}
                submitLabel={submit_label}
            />

            <ResultsList
                results={state.results}
                dispatch={dispatch}
                vatAddedLabel={results_vat_added_label}
                vatRemovedLabel={results_vat_removed_label}
                amountLabel={amount_label}
                totalGrossLabel={total_gross_label}
                totalNetLabel={total_net_label}
                title={results_title}
            />
        </Section>
    )
}

export const vatCalculatorFragment = graphql`
    fragment VatCalculator on PrismicVatCalculator {
        __typename
        data {
            title
            body {
                richText
            }
            vat_label
            amount_label
            add_vat_label
            remove_vat_label
            total_net_label
            total_gross_label
            submit_label
            results_title
            results_vat_added_label
            results_vat_removed_label
        }
    }
`
